@use '../../../styles/mixins' as *;

.container {
  position: fixed;
  top: env(safe-area-inset-top, 0);
  z-index: 10000;
  @include app-frame-fixed;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  pointer-events: none;
}

// High-contrast toast per Figma node 24135:100972. A solid pill with white
// text and a solid, semantically-coloured status icon (icon colour is set in
// `ToastContainer`). success / info / warning share the dark surface; error
// uses the solid red surface. All colours are tokens, so light/dark theming
// is automatic.
.toast {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  // pl 16 / pr 8 / py 8 per Figma.
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md)
    var(--spacing-xl);
  border-radius: var(--radius-md);
  background: var(--colors-background-gray-primary-solid);
  color: var(--colors-text-white-inverse);
  @include elevation(lg);
  pointer-events: auto;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
}

// Icon + primary line + close button, all vertically centred on one row so a
// single-line toast (and the first line of a multi-line one) stays aligned
// regardless of the 32 px close button height.
.headerRow {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.primaryText {
  flex: 1;
  min-width: 0;
}

// Message shown beneath a title — left edge lines up with the title (past the
// 20 px icon + row gap), so title and message share the same x-axis.
.secondaryText {
  padding-left: calc(var(--spacing-2xl) + var(--spacing-md));
}

.dismiss {
  appearance: none;
  background: transparent;
  border: 0;
  color: var(--colors-text-white-inverse);
  cursor: pointer;
  border-radius: var(--radius-md);
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

// success / info / warning inherit the dark pill from `.toast`; only error
// swaps to the solid red surface.
.variant-error {
  background: var(--colors-background-alerts-error-solid);
}

// ---------------------------------------------------------------------------
// Default (low-contrast) variant — Showroom reference only.
// ---------------------------------------------------------------------------
// The app uses the high-contrast style above everywhere. This mirrors the
// Figma "default" column (light tinted surface + dark text + coloured icon)
// so the design system reference can show both treatments.
.contrastDefault {
  color: var(--colors-text-primary);
  border: 1px solid var(--colors-border-primary);
  background: var(--colors-background-gray-primary);

  .dismiss {
    color: var(--colors-text-tertiary);
    opacity: 1;

    &:hover {
      color: var(--colors-text-primary);
    }
  }

  &.variant-success {
    background: var(--component-utility-success-50);
    border-color: var(--colors-border-success-subtle);
  }

  &.variant-error {
    background: var(--component-utility-error-50);
    border-color: var(--colors-border-error-subtle);
  }

  &.variant-info {
    background: var(--component-utility-info-50);
    border-color: var(--colors-border-info-subtle);
  }

  &.variant-warning {
    background: var(--component-utility-warning-50);
    border-color: var(--colors-border-warning-subtle);
  }
}
